import { localWallTimeToIso } from '../../utils/activityDateTime';
import type { AbsenceKind } from './plantafelTypes';
import {
  absenceKindToEntryType,
  PLANTAFEL_ABSENCE_ENTRY_TYPES,
  type PlantafelAbsenceDraft,
} from './plantafelAbsenceMapping';

export type CalendarListAbsenceBlock = {
  id: string;
  employee_id: string;
  entry_date: string;
  entry_type: string;
  absence_kind: AbsenceKind | 'other';
  holiday_name?: string | null;
  start_time?: string | null;
  end_time?: string | null;
};

export type CalendarListAbsenceRow = {
  id: string;
  employee_id: string;
  date_from: string;
  date_to: string;
  entry_type: string;
  absence_kind: AbsenceKind;
  title: string;
  /** First concrete time-entry id for editing; null for merged/holiday-only rows. */
  entry_id: string | null;
  start_time: string | null;
  end_time: string | null;
};

const ABSENCE_TITLES: Record<AbsenceKind, string> = {
  urlaub: 'Urlaub',
  krank: 'Krank (AU)',
  feiertag: 'Feiertag',
  freischicht: 'Freischicht',
  stundenabbau: 'Freizeitausgleich',
  fehlzeit: 'Unbezahlte Fehlzeit',
  sonderurlaub: 'Sonderurlaub',
  krankengeld: 'Krankengeld',
};

function dayPlusOne(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function absenceTitle(kind: AbsenceKind, holidayName?: string | null): string {
  if (kind === 'feiertag' && holidayName?.trim()) return holidayName.trim();
  return ABSENCE_TITLES[kind];
}

function isConcreteEntryId(id: string): boolean {
  return Boolean(id) && !id.startsWith('holiday-') && !id.startsWith('abs-merge-');
}

/** Merge consecutive same-kind absences per employee into list rows. */
export function mergeAbsenceBlocksForList(blocks: CalendarListAbsenceBlock[]): CalendarListAbsenceRow[] {
  const usable = blocks.filter((b) => b.absence_kind !== 'other');
  const groups = new Map<string, CalendarListAbsenceBlock[]>();
  for (const b of usable) {
    const key = `${b.employee_id}:${b.absence_kind}:${b.holiday_name ?? ''}`;
    const list = groups.get(key) ?? [];
    list.push(b);
    groups.set(key, list);
  }

  const rows: CalendarListAbsenceRow[] = [];
  for (const list of groups.values()) {
    const sorted = [...list].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
    let current = sorted[0];
    let dateTo = current.entry_date;
    let entryId = isConcreteEntryId(current.id) ? current.id : null;
    let title = absenceTitle(current.absence_kind as AbsenceKind, current.holiday_name);

    const flush = () => {
      rows.push({
        id: `abs-list-${current.employee_id}-${current.absence_kind}-${current.entry_date}-${dateTo}`,
        employee_id: current.employee_id,
        date_from: current.entry_date,
        date_to: dateTo,
        entry_type: current.entry_type,
        absence_kind: current.absence_kind as AbsenceKind,
        title,
        entry_id: entryId,
        start_time: current.start_time ?? null,
        end_time: current.end_time ?? null,
      });
    };

    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      const adjacent = dayPlusOne(dateTo) === next.entry_date;
      const sameTimed =
        (current.start_time ?? null) === (next.start_time ?? null) &&
        (current.end_time ?? null) === (next.end_time ?? null);
      // Only merge full-day (no clock times) adjacent spans
      const canMerge = adjacent && !current.start_time && !next.start_time && sameTimed;
      if (canMerge) {
        dateTo = next.entry_date;
        if (!entryId && isConcreteEntryId(next.id)) entryId = next.id;
        continue;
      }
      flush();
      current = next;
      dateTo = next.entry_date;
      entryId = isConcreteEntryId(next.id) ? next.id : null;
      title = absenceTitle(next.absence_kind as AbsenceKind, next.holiday_name);
    }
    flush();
  }

  return rows.sort((a, b) => a.date_from.localeCompare(b.date_from) || a.title.localeCompare(b.title, 'de'));
}

export function absenceListTypeLabel(kind: AbsenceKind | string): string | null {
  if (kind === 'feiertag') return 'Feiertag';
  const byKind = PLANTAFEL_ABSENCE_ENTRY_TYPES.find((o) => o.kind === kind);
  if (byKind) return byKind.label;
  const byEntry = PLANTAFEL_ABSENCE_ENTRY_TYPES.find((o) => o.entryType === kind);
  return byEntry?.label ?? null;
}

export function absenceRowToDueEnd(
  row: CalendarListAbsenceRow,
  timeZone?: string,
): { due_at: string; end_at: string } {
  if (row.start_time && row.end_time && row.date_from === row.date_to) {
    return {
      due_at: localWallTimeToIso(row.date_from, String(row.start_time).slice(0, 5), timeZone),
      end_at: localWallTimeToIso(row.date_to, String(row.end_time).slice(0, 5), timeZone),
    };
  }
  return {
    due_at: localWallTimeToIso(row.date_from, '00:00', timeZone),
    end_at: localWallTimeToIso(dayPlusOne(row.date_to), '00:00', timeZone),
  };
}

export function absenceRowToDraft(
  row: CalendarListAbsenceRow,
  opts?: { readOnly?: boolean },
): PlantafelAbsenceDraft | null {
  const entryType = absenceKindToEntryType(row.absence_kind);
  if (!entryType) return null;
  return {
    employeeId: row.employee_id,
    dateFrom: row.date_from,
    dateTo: row.date_to,
    entryType,
    startTime: row.start_time ? String(row.start_time).slice(0, 5) : '',
    endTime: row.end_time ? String(row.end_time).slice(0, 5) : '',
    breakMinutes: '',
    note: '',
    entryId: row.entry_id,
    readOnly: opts?.readOnly ?? row.absence_kind === 'feiertag',
  };
}
